import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { FaStar, FaQuoteRight } from 'react-icons/fa';
import './css/ReviewsPage.css';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    rating: 5,
    text: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/reviews`)
      .then((res) => setReviews(res.data.reviews || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (value) => {
    setForm((prev) => ({ ...prev, rating: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage(null);
    setSubmitError(null);

    if (!form.name.trim() || !form.text.trim()) {
      setSubmitError('الاسم ونص التقييم مطلوبان.');
      return;
    }

    if (!form.email.trim()) {
      setSubmitError('البريد الإلكتروني مطلوب للتواصل عند الحاجة.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/reviews`, {
        customer_name: form.name.trim(),
        email: form.email.trim(),
        rating: form.rating,
        review_text: form.text.trim(),
      });
      setSubmitMessage('شكرًا لك! تم استلام تقييمك وسيتم مراجعته قبل النشر.');
      setForm({
        name: '',
        email: '',
        rating: 5,
        text: '',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Review submit error', err);
      setSubmitError(
        err.response?.data?.error ||
          'حدث خطأ أثناء إرسال التقييم. برجاء المحاولة مرة أخرى.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const Stars = ({ rating }) => {
    const n = Math.min(5, Math.max(0, Number(rating) || 0));
    return (
      <span className="review-stars" aria-label={`${n} نجوم`}>
        {[1, 2, 3, 4, 5].map((i) => (
          <FaStar key={i} className={i <= n ? 'star filled' : 'star'} />
        ))}
      </span>
    );
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <div className="reviews-page" dir="rtl">
      <header className="reviews-header">
        <div className="container relative-z">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            آراء العملاء
          </motion.h1>
          <p>ما قالته الطالبات عن تجربتهن مع د. نهى</p>
        </div>
      </header>

      <section className="reviews-content">
        <div className="container">
          <section className="review-form-section">
            <h2 className="review-form-title">أضيفي تقييمك</h2>
            <p className="review-form-subtitle">
              شاركي تجربتك مع د. نهى. بريدك الإلكتروني لن يظهر للعامة.
            </p>
            <form className="review-form" onSubmit={handleSubmit} noValidate>
              <div className="review-form-row">
                <div className="review-form-group">
                  <label htmlFor="review-name">الاسم *</label>
                  <input
                    id="review-name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="اكتبي اسمك الأول"
                    required
                  />
                </div>
                <div className="review-form-group">
                  <label htmlFor="review-email">البريد الإلكتروني *</label>
                  <input
                    id="review-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="لن يظهر في صفحة التقييمات"
                    required
                  />
                </div>
              </div>

              <div className="review-form-row">
                <div className="review-form-group review-form-rating">
                  <label>عدد النجوم *</label>
                  <div className="review-form-stars">
                    {[1, 2, 3, 4, 5].map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={
                          v <= Number(form.rating)
                            ? 'rating-star filled'
                            : 'rating-star'
                        }
                        onClick={() => handleRatingClick(v)}
                        aria-label={`${v} نجوم`}
                      >
                        <FaStar />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="review-form-group">
                <label htmlFor="review-text">نص التقييم *</label>
                <textarea
                  id="review-text"
                  name="text"
                  value={form.text}
                  onChange={handleChange}
                  rows="4"
                  placeholder="اكتبي تجربتك مع د. نهى..."
                  required
                />
              </div>

              {submitError && (
                <p className="review-form-error">{submitError}</p>
              )}
              {submitMessage && (
                <p className="review-form-success">{submitMessage}</p>
              )}

              <button
                type="submit"
                className="review-form-submit"
                disabled={submitting}
              >
                {submitting ? 'جارٍ إرسال التقييم...' : 'إرسال التقييم'}
              </button>
            </form>
          </section>

          {loading && <p className="reviews-loading">جاري تحميل التقييمات...</p>}
          {error && <p className="reviews-error">حدث خطأ في التحميل.</p>}
          {!loading && !error && reviews.length === 0 && (
            <p className="reviews-empty">لا توجد تقييمات حتى الآن.</p>
          )}
          {!loading && !error && reviews.length > 0 && (
            <div className="reviews-grid">
              {reviews.map((review, i) => (
                <motion.article
                  key={review.id}
                  className={`review-card note-card note-${(i % 6) + 1}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <span className="review-pin" aria-hidden="true" />
                  <FaQuoteRight className="review-quote-icon" />
                  <Stars rating={review.rating} />
                  <p className="review-text">{review.review_text}</p>
                  <div className="review-author">
                    <span className="review-name">{review.customer_name}</span>
                    {formatDate(review.created_at) && (
                      <span className="review-date">{formatDate(review.created_at)}</span>
                    )}
                  </div>
                  <Link to="/booking" className="review-cta">
                    احجزي استشارتك
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
